/*
 * Evernote search syntax → SQL WHERE clause over `notes n`.
 * Runs identically against the client replica and D1.
 *
 * Text matching is case-insensitive substring (LIKE), not tokenized full-text:
 * it handles Korean (particles attach to words, so "회의" must match "회의록을")
 * and the whole corpus is a few MB, so a scan is fast.
 *
 * With `wordStart` (the client registers an `engram_word(text, term)` SQL function), a term that starts with a
 * Latin letter or digit must start a word, like Evernote: "gio" finds "Gio" and "Giovanni", not "region".
 */

export type Node =
  | { k: "and"; c: Node[] }
  | { k: "or"; c: Node[] }
  | { k: "not"; c: Node }
  | { k: "text"; v: string; title: boolean }
  | { k: "notebook" | "stack" | "tag" | "source" | "resource" | "contains" | "todo"; v: string }
  | { k: "created" | "updated"; v: string };

const KEYS = new Set([
  "intitle", "notebook", "stack", "tag", "created", "updated", "todo", "contains",
  "resource", "source", "sourceurl", "any",
]);

interface Tok {
  type: "word" | "lp" | "rp" | "or" | "and" | "not";
  neg?: boolean;
  key?: string;
  value?: string;
}

export function tokenize(q: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < q.length) {
    const ch = q[i]!;
    if (/\s/.test(ch)) { i++; continue; }
    if (ch === "(") { toks.push({ type: "lp" }); i++; continue; }
    if (ch === ")") { toks.push({ type: "rp" }); i++; continue; }
    let raw = "";
    let inQ = false;
    while (i < q.length) {
      const c = q[i]!;
      if (c === '"') inQ = !inQ;
      else if (!inQ && (/\s/.test(c) || c === "(" || c === ")")) break;
      raw += c;
      i++;
    }
    if (raw === "OR") { toks.push({ type: "or" }); continue; }
    if (raw === "AND") { toks.push({ type: "and" }); continue; }
    if (raw === "NOT" || raw === "-") { toks.push({ type: "not" }); continue; }
    let neg = false;
    if (raw.startsWith("-")) { neg = true; raw = raw.slice(1); }
    const m = /^([a-zA-Z]+):(.*)$/s.exec(raw);
    if (m && KEYS.has(m[1]!.toLowerCase())) {
      toks.push({ type: "word", neg, key: m[1]!.toLowerCase(), value: unquote(m[2]!) });
    } else {
      toks.push({ type: "word", neg, value: unquote(raw) });
    }
  }
  return toks;
}

function unquote(s: string): string {
  return s.replace(/^"/, "").replace(/"$/, "");
}

export interface ParsedQuery {
  ast: Node | null;
  /** Positive plain-text terms, for highlighting hits in the note. */
  terms: string[];
}

/**
 * Longer queries are cut: SQLite caps LIKE patterns and expression depth, and a pasted page of text
 * isn't a search anyone means. Nobody types 100 terms.
 */
export const MAX_QUERY_CHARS = 1000;
export const MAX_QUERY_TOKENS = 100;

export function parseQuery(q: string): ParsedQuery {
  const toks = tokenize(q.slice(0, MAX_QUERY_CHARS).normalize("NFC")).slice(0, MAX_QUERY_TOKENS);
  let any = false;
  if (toks[0]?.type === "word" && toks[0].key === "any" && !toks[0].value) {
    any = true;
    toks.shift();
  }
  let pos = 0;
  const terms: string[] = [];

  function orExpr(): Node | null {
    const parts: Node[] = [];
    const first = andExpr();
    if (first) parts.push(first);
    while (toks[pos]?.type === "or") {
      pos++;
      const n = andExpr();
      if (n) parts.push(n);
    }
    return parts.length === 0 ? null : parts.length === 1 ? parts[0]! : { k: "or", c: parts };
  }

  function andExpr(): Node | null {
    const parts: Node[] = [];
    for (;;) {
      const t = toks[pos];
      if (!t || t.type === "rp" || t.type === "or") break;
      if (t.type === "and") { pos++; continue; }
      const n = unary(false);
      if (n) parts.push(n);
    }
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0]!;
    return { k: any ? "or" : "and", c: parts };
  }

  function unary(negated: boolean): Node | null {
    const t = toks[pos];
    if (!t) return null;
    if (t.type === "not") {
      pos++;
      const n = unary(!negated);
      return n ? { k: "not", c: n } : null;
    }
    if (t.type === "lp") {
      pos++;
      const n = orExpr();
      if (toks[pos]?.type === "rp") pos++;
      return n;
    }
    if (t.type === "rp") { pos++; return null; }
    pos++;
    const n = leaf(t, negated !== !!t.neg);
    if (!n) return null;
    return t.neg ? { k: "not", c: n } : n;
  }

  function leaf(t: Tok, negative: boolean): Node | null {
    const v = (t.value ?? "").trim();
    if (!t.key) {
      if (!v) return null;
      const text = v.replace(/\*+$/, "");
      if (!text) return null;
      if (!negative) terms.push(text);
      return { k: "text", v: text, title: false };
    }
    switch (t.key) {
      case "intitle": {
        const text = v.replace(/\*+$/, "");
        if (!text) return null;
        if (!negative) terms.push(text);
        return { k: "text", v: text, title: true };
      }
      case "sourceurl":
        return v ? { k: "source", v } : null;
      case "any":
        return null;
      default:
        return v ? ({ k: t.key, v } as Node) : null;
    }
  }

  const parts: Node[] = [];
  while (pos < toks.length) {
    const n = orExpr();
    if (n) parts.push(n);
    if (toks[pos]?.type === "rp") pos++; // stray ')'
  }
  const ast = parts.length === 0 ? null : parts.length === 1 ? parts[0]! : { k: any ? "or" : "and", c: parts } as Node;
  return { ast, terms };
}

export interface CompileOptions {
  now?: Date;
  weekStartsOn?: number; // 0 = Sunday (Evernote default)
  wordStart?: boolean; // needs the engram_word() SQL function (see the top of this file)
  /** Answer HTML questions (contains:table, todo:true…) from the client's note_flags table instead of scanning bodies. */
  flags?: boolean;
}

/**
 * What `contains:` and `todo:` look for in a note's HTML, as bits. The client keeps them per note in note_flags
 * (worker.ts triggers run `noteFlagsSql`), so those searches never read note bodies; D1 runs the LIKEs directly.
 */
export const HTML_FLAGS = {
  table: [1, "%<table%"],
  link: [2, "%<a %"],
  pre: [4, "%<pre%"],
  code: [8, "%<code%"],
  checklist: [16, '%data-type="taskList"%'],
  checked: [32, '%data-checked="true"%'],
  unchecked: [64, '%data-checked="false"%'],
  enCrypt: [128, "%data-en-crypt%"],
  encrypted: [256, "%data-encrypted%"],
} as const;
type HtmlFlag = keyof typeof HTML_FLAGS;

/** The note_flags value for an HTML column, e.g. `noteFlagsSql("new.body_html")`. */
export function noteFlagsSql(col: string): string {
  return Object.values(HTML_FLAGS)
    .map(([bit, pat]) => `(CASE WHEN ${col} LIKE '${pat}' THEN ${bit} ELSE 0 END)`)
    .join(" + ");
}

function htmlTest(opts: CompileOptions, ...flags: HtmlFlag[]): string {
  if (opts.flags) return `n.id IN (SELECT id FROM note_flags WHERE f & ${flags.reduce((m, f) => m | HTML_FLAGS[f][0], 0)})`;
  const tests = flags.map((f) => `n.body_html LIKE '${HTML_FLAGS[f][1]}'`);
  return tests.length > 1 ? `(${tests.join(" OR ")})` : tests[0]!;
}

/** Terms that must start a word under `wordStart`: Latin letters and digits (not Hangul, CJK, etc.). */
export function isWordStartTerm(v: string): boolean {
  return /^[\p{Script=Latin}\p{N}]/u.test(v);
}

export interface CompiledQuery {
  where: string;
  params: (string | number)[];
  terms: string[];
}

/** Escape LIKE wildcards; `*` in user input becomes a wildcard. */
function likeEscape(s: string): string {
  return s.replace(/[\\%_]/g, (c) => "\\" + c);
}
function likePattern(v: string, mode: "exact" | "contains"): string {
  const star = v.endsWith("*");
  const base = likeEscape(v.replace(/\*+$/, "")).replace(/\*/g, "%");
  if (mode === "contains") return `%${base}%`;
  return star ? `${base}%` : base;
}
const ESC = ` ESCAPE '\\'`;

const CONTAINS: Record<string, string> = {
  task: `n.id IN (SELECT tk.note_id FROM tasks tk WHERE tk.note_id IS NOT NULL AND tk.deleted = 0)`,
  taskcompleted: `n.id IN (SELECT tk.note_id FROM tasks tk WHERE tk.note_id IS NOT NULL AND tk.deleted = 0 AND tk.completed_at IS NOT NULL)`,
  tasknotcompleted: `n.id IN (SELECT tk.note_id FROM tasks tk WHERE tk.note_id IS NOT NULL AND tk.deleted = 0 AND tk.completed_at IS NULL)`,
  attachment: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0)`,
  file: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0)`,
  image: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0 AND a.mime LIKE 'image/%')`,
  pdf: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0 AND a.mime = 'application/pdf')`,
  audio: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0 AND a.mime LIKE 'audio/%')`,
  video: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0 AND a.mime LIKE 'video/%')`,
  document: `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0 AND a.mime NOT LIKE 'image/%' AND a.mime NOT LIKE 'audio/%' AND a.mime NOT LIKE 'video/%')`,
};
/** contains: keys answered from the HTML. */
const CONTAINS_HTML: Record<string, HtmlFlag[]> = {
  table: ["table"],
  url: ["link"],
  link: ["link"],
  codeblock: ["pre"],
  code: ["pre", "code"],
  checklist: ["checklist"],
  todo: ["checklist"],
  encryption: ["enCrypt", "encrypted"],
};

export function compileQuery(q: string, opts: CompileOptions = {}): CompiledQuery {
  const { ast, terms } = parseQuery(q);
  const params: (string | number)[] = [];
  const now = opts.now ?? new Date();

  function c(n: Node): string {
    switch (n.k) {
      case "and":
        return "(" + n.c.map(c).join(" AND ") + ")";
      case "or":
        return "(" + n.c.map(c).join(" OR ") + ")";
      case "not":
        return `NOT ${c(n.c)}`;
      case "text": {
        const p = likePattern(n.v, "contains");
        const word = opts.wordStart && isWordStartTerm(n.v);
        const w = n.v.replace(/\*+$/, "");
        if (n.title) {
          params.push(p);
          if (!word) return `(n.title LIKE ?${ESC})`;
          params.push(w);
          return `(n.title LIKE ?${ESC} AND engram_word(n.title, ?))`;
        }
        if (!word) {
          params.push(p, p, p);
          return `(n.title LIKE ?${ESC} OR n.body_text LIKE ?${ESC} OR n.id IN (SELECT tk.note_id FROM tasks tk WHERE tk.note_id IS NOT NULL AND tk.deleted = 0 AND tk.title LIKE ?${ESC}))`;
        }
        // LIKE narrows the scan; engram_word checks the match starts a word.
        params.push(p, w, p, w, p, w);
        return `((n.title LIKE ?${ESC} AND engram_word(n.title, ?)) OR (n.body_text LIKE ?${ESC} AND engram_word(n.body_text, ?)) OR n.id IN (SELECT tk.note_id FROM tasks tk WHERE tk.note_id IS NOT NULL AND tk.deleted = 0 AND tk.title LIKE ?${ESC} AND engram_word(tk.title, ?)))`;
      }
      case "notebook":
        params.push(likePattern(n.v, "exact"));
        return `n.notebook_id IN (SELECT nb.id FROM notebooks nb WHERE nb.deleted = 0 AND nb.kind = 'notebook' AND nb.name LIKE ?${ESC})`;
      case "stack":
        params.push(likePattern(n.v, "exact"));
        // Notebooks anywhere under the stack (stacks can nest when the owner turns that on).
        return `n.notebook_id IN (WITH RECURSIVE sub(id) AS (SELECT st.id FROM notebooks st WHERE st.deleted = 0 AND st.kind = 'stack' AND st.name LIKE ?${ESC} UNION SELECT nb.id FROM notebooks nb JOIN sub ON nb.parent_id = sub.id WHERE nb.deleted = 0) SELECT id FROM sub)`;
      case "tag":
        if (n.v === "*") {
          return `EXISTS (SELECT 1 FROM json_each(n.tag_ids) je JOIN tags tg ON tg.id = je.value WHERE tg.deleted = 0)`;
        }
        params.push(likePattern(n.v, "exact"));
        return `EXISTS (SELECT 1 FROM json_each(n.tag_ids) je JOIN tags tg ON tg.id = je.value WHERE tg.deleted = 0 AND tg.name LIKE ?${ESC})`;
      case "source":
        if (n.v === "*") return `(n.source_url IS NOT NULL AND n.source_url <> '')`;
        params.push(likePattern(n.v, n.v.endsWith("*") ? "exact" : "contains"));
        return `(n.source_url LIKE ?${ESC})`;
      case "resource": {
        const v = n.v.toLowerCase();
        if (v === "*") return CONTAINS.attachment!;
        params.push(likePattern(v, "exact"));
        return `n.id IN (SELECT a.note_id FROM attachments a WHERE a.note_id IS NOT NULL AND a.deleted = 0 AND a.mime LIKE ?${ESC})`;
      }
      case "contains": {
        const key = n.v.toLowerCase();
        const html = CONTAINS_HTML[key];
        return html ? htmlTest(opts, ...html) : (CONTAINS[key] ?? "0");
      }
      case "todo": {
        const v = n.v.toLowerCase();
        if (v === "true") return htmlTest(opts, "checked");
        if (v === "false") return htmlTest(opts, "unchecked");
        return htmlTest(opts, "checklist");
      }
      case "created":
      case "updated": {
        const t = parseDateSpec(n.v, now, opts.weekStartsOn ?? 0);
        if (t == null) return "1";
        params.push(t);
        return `n.${n.k} >= ?`;
      }
    }
  }

  const where = ast ? c(ast) : "1";
  return { where, params, terms };
}

/**
 * Evernote date values: YYYYMMDD, YYYYMMDDTHHMMSS[Z], day, week, month, year,
 * each optionally with -N (e.g. day-2). Returns epoch ms, local time.
 */
export function parseDateSpec(v: string, now: Date, weekStartsOn = 0): number | null {
  const s = v.trim().toLowerCase();
  let m = /^(\d{4})(\d{2})(\d{2})(?:t(\d{2})(\d{2})(\d{2})(z)?)?$/.exec(s);
  if (m) {
    const [y, mo, d, h = "0", mi = "0", se = "0"] = m.slice(1, 7).map((x) => x ?? "0") as string[];
    if (m[7]) return Date.UTC(+y!, +mo! - 1, +d!, +h, +mi, +se);
    return new Date(+y!, +mo! - 1, +d!, +h, +mi, +se).getTime();
  }
  if (s === "today") return startOf("day", 0, now, weekStartsOn);
  if (s === "yesterday") return startOf("day", 1, now, weekStartsOn);
  m = /^(day|week|month|year)(?:-(\d+))?$/.exec(s);
  if (!m) return null;
  return startOf(m[1] as "day", m[2] ? +m[2] : 0, now, weekStartsOn);
}

function startOf(unit: "day" | "week" | "month" | "year", back: number, now: Date, weekStartsOn: number): number {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (unit) {
    case "day":
      d.setDate(d.getDate() - back);
      break;
    case "week":
      d.setDate(d.getDate() - ((d.getDay() - weekStartsOn + 7) % 7) - 7 * back);
      break;
    case "month":
      d.setDate(1);
      d.setMonth(d.getMonth() - back);
      break;
    case "year":
      d.setMonth(0, 1);
      d.setFullYear(d.getFullYear() - back);
      break;
  }
  return d.getTime();
}
